import type { PropsWithChildren } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { canAccessManagementPath } from "@/modules/auth/backOffice";
import { landingPathFor } from "@/modules/auth/landing";
import { useSession } from "@/modules/auth/useSession";
import { ManageIndexRedirect } from "./ManageIndexRedirect";

/**
 * The back office's door (`modules/auth/backOffice.ts`): a floor role that
 * reaches any `/manage/*` URL — typed, bookmarked, or left in the history — is
 * sent to its own surface before a page or navigation mounts. Cashier pages
 * share the layout, but both their routes and navigation are restricted.
 */
export function BackOfficeGate({ children }: PropsWithChildren) {
  const { locale = "ar" } = useParams();
  const { pathname } = useLocation();
  const { data: session, isLoading } = useSession();
  if (isLoading || !session) return null;
  const path = pathname.slice(`/${locale}/`.length).replace(/\/+$/, "");
  if (!canAccessManagementPath(session.seller.permissions, path)) {
    const landing = landingPathFor(locale, session);
    // An account with no working surface gets the existing no-access recovery,
    // without a shell or a redirect back to this same gate.
    if (landing === pathname) return <ManageIndexRedirect />;
    return <Navigate to={landing} replace />;
  }
  return children;
}
